import React, { useState, useEffect } from 'react';
import { formatNumber, formatKcal, formatPercentage } from '../utils/numberFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Plus, ArrowLeft, Loader2, X, Edit, Trash2, ChevronDown, ChevronRight, Beaker, FileSpreadsheet, FileDown, FileUp, Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import { foodAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import ConfirmDialog, { AlertDialog } from '../components/ConfirmDialog';
import { useConfirmDialog, useAlertDialog } from '../hooks/useDialog';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import Navbar from '../components/Navbar';
import { exportFoodsToExcel, downloadFoodTemplateExcel, parseFoodExcel } from '../utils/databaseExcelHelper';

export default function FoodDatabase() {
  const navigate = useNavigate();
  const { dialog: confirmDialog, confirm, close: closeConfirm } = useConfirmDialog();
  const { dialog: alertDialog, alert: showAlert, close: closeAlert } = useAlertDialog();
  
  const [foods, setFoods] = useState([]);
  const [filteredFoods, setFilteredFoods] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingFood, setEditingFood] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Micronutrient editor state
  const [showMicroEditor, setShowMicroEditor] = useState(false);
  const [editingMicroFood, setEditingMicroFood] = useState(null);
  const [microData, setMicroData] = useState({
    vitamins: {},
    minerals: {},
    electrolytes: {},
    fatty_acids: {}
  });
  const [savingMicro, setSavingMicro] = useState(false);
  
  // Excel Import state for Admin
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [parsedImportData, setParsedImportData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');

  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  // Add custom food form
  const getEmptyFoodForm = () => ({
    name: '',
    unit: '100g',
    energy: '',
    protein: '',
    carbs: '',
    fat: '',
    fiber: '0',
    // Vitamins
    vitamin_a: '0', vitamin_d3: '0', vitamin_e: '0', vitamin_k: '0',
    vitamin_b1: '0', vitamin_b2: '0', vitamin_b3: '0', vitamin_b5: '0',
    vitamin_b6: '0', vitamin_b12: '0', folic_acid: '0', biotin: '0',
    choline: '0', vitamin_c: '0',
    // Minerals
    calcium: '0', phosphorus: '0', magnesium: '0', iron: '0',
    iodine: '0', zinc: '0', selenium: '0', manganese: '0',
    fluoride: '0', chromium: '0', copper: '0',
    // Electrolytes
    sodium: '0', potassium: '0', chloride: '0',
    // Fatty Acids
    omega3: '0', omega6: '0',
  });
  
  const [newFood, setNewFood] = useState(getEmptyFoodForm());
  const [showMicronutrients, setShowMicronutrients] = useState(false);
  
  // Edit food form
  const [editFood, setEditFood] = useState(getEmptyFoodForm());
  const [showEditMicronutrients, setShowEditMicronutrients] = useState(false);

  useEffect(() => {
    loadFoods();
  }, []);

  useEffect(() => {
    if (search.trim() === '') {
      setFilteredFoods(foods);
    } else {
      const filtered = foods.filter(food =>
        food.name.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredFoods(filtered);
    }
  }, [search, foods]);

  const loadFoods = async () => {
    setLoading(true);
    try {
      const data = await foodAPI.getAll();
      setFoods(data);
      setFilteredFoods(data);
    } catch (error) {
      console.error('Error loading foods:', error);
      showAlert({
        type: 'danger',
        title: 'Error',
        message: 'Failed to load foods. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomFood = async () => {
    if (!newFood.name || !newFood.energy) {
      showAlert({
        type: 'warning',
        title: 'Missing Information',
        message: 'Please enter at least name and energy'
      });
      return;
    }

    setAdding(true);
    try {
      await foodAPI.create({
        name: newFood.name,
        unit: newFood.unit,
        macronutrients: {
          energy_kcal: parseFloat(newFood.energy),
          protein_g: parseFloat(newFood.protein || 0),
          carbohydrate_g: parseFloat(newFood.carbs || 0),
          fat_g: parseFloat(newFood.fat || 0),
          fiber_g: parseFloat(newFood.fiber || 0),
        },
      });

      showAlert({
        type: 'success',
        title: 'Success',
        message: 'Custom food added successfully!'
      });
      setShowAddDialog(false);
      setNewFood({ name: '', unit: '100g', energy: '', protein: '', carbs: '', fat: '', fiber: '' });
      setSearch('');
      await loadFoods();
    } catch (error) {
      console.error('Error adding food:', error);
      showAlert({
        type: 'danger',
        title: 'Error',
        message: 'Failed to add custom food'
      });
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (foodId, foodName, isCustom) => {
    // Admin can delete any food, non-admin can only delete custom foods
    if (!isAdmin && !isCustom) {
      showAlert({
        type: 'warning',
        title: 'Cannot Delete',
        message: 'Cannot delete system foods. Only custom foods can be deleted.'
      });
      return;
    }

    confirm({
      type: 'danger',
      title: 'Delete Food',
      message: `Delete "${foodName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await foodAPI.delete(foodId);
          showAlert({
            type: 'success',
            title: 'Deleted',
            message: 'Food deleted successfully'
          });
          setSearch('');
          await loadFoods();
        } catch (error) {
          console.error('Error deleting food:', error);
          showAlert({
            type: 'danger',
            title: 'Error',
            message: 'Failed to delete food'
          });
        }
      }
    });
  };
  
  const handleEdit = (food) => {
    setEditingFood(food);
    const v = food.vitamins || {};
    const m = food.minerals || {};
    const e = food.electrolytes || {};
    const f = food.fatty_acids || {};
    
    setEditFood({
      name: food.name,
      unit: food.unit,
      energy: food.macronutrients.energy_kcal.toString(),
      protein: food.macronutrients.protein_g.toString(),
      carbs: food.macronutrients.carbohydrate_g.toString(),
      fat: food.macronutrients.fat_g.toString(),
      fiber: (food.macronutrients.fiber_g || 0).toString(),
      // Vitamins
      vitamin_a: (v.vitamin_a_mcg || 0).toString(),
      vitamin_d3: (v.vitamin_d3_mcg || 0).toString(),
      vitamin_e: (v.vitamin_e_mcg || 0).toString(),
      vitamin_k: (v.vitamin_k_mcg || 0).toString(),
      vitamin_b1: (v.vitamin_b1_mg || 0).toString(),
      vitamin_b2: (v.vitamin_b2_mg || 0).toString(),
      vitamin_b3: (v.vitamin_b3_mg || 0).toString(),
      vitamin_b5: (v.vitamin_b5_mg || 0).toString(),
      vitamin_b6: (v.vitamin_b6_mg || 0).toString(),
      vitamin_b12: (v.vitamin_b12_mcg || 0).toString(),
      folic_acid: (v.folic_acid_mcg || 0).toString(),
      biotin: (v.biotin_mcg || 0).toString(),
      choline: (v.choline_mg || 0).toString(),
      vitamin_c: (v.vitamin_c_mg || 0).toString(),
      // Minerals
      calcium: (m.calcium_mg || 0).toString(),
      phosphorus: (m.phosphorus_mg || 0).toString(),
      magnesium: (m.magnesium_mg || 0).toString(),
      iron: (m.iron_mg || 0).toString(),
      iodine: (m.iodine_mcg || 0).toString(),
      zinc: (m.zinc_mg || 0).toString(),
      selenium: (m.selenium_mcg || 0).toString(),
      manganese: (m.manganese_mg || 0).toString(),
      fluoride: (m.fluoride_mg || 0).toString(),
      chromium: (m.chromium_mcg || 0).toString(),
      copper: (m.copper_mg || 0).toString(),
      // Electrolytes
      sodium: (e.sodium_mg || 0).toString(),
      potassium: (e.potassium_mg || 0).toString(),
      chloride: (e.chloride_mg || 0).toString(),
      // Fatty Acids
      omega3: (f.omega3_mg || 0).toString(),
      omega6: (f.omega6_mg || 0).toString(),
    });
    setShowEditDialog(true);
  };
  
  const openMicroEditor = (food) => {
    setEditingMicroFood(food);
    setMicroData({
      vitamins: food.vitamins || {},
      minerals: food.minerals || {},
      electrolytes: food.electrolytes || {},
      fatty_acids: food.fatty_acids || {}
    });
    setShowMicroEditor(true);
  };
  
  const handleSaveMicronutrients = async () => {
    if (!editingMicroFood) return;
    
    setSavingMicro(true);
    try {
      await foodAPI.update(editingMicroFood.id, {
        name: editingMicroFood.name,
        unit: editingMicroFood.unit,
        macronutrients: editingMicroFood.macronutrients,
        vitamins: microData.vitamins,
        minerals: microData.minerals,
        electrolytes: microData.electrolytes,
        fatty_acids: microData.fatty_acids,
      });
      
      showAlert({
        type: 'success',
        title: 'Success',
        message: 'Micronutrients updated successfully!'
      });
      
      setShowMicroEditor(false);
      await loadFoods();
    } catch (error) {
      console.error('Error updating micronutrients:', error);
      showAlert({
        type: 'danger',
        title: 'Error',
        message: 'Failed to update micronutrients'
      });
    } finally {
      setSavingMicro(false);
    }
  };
  
  const toggleExpand = (foodId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(foodId)) {
      newExpanded.delete(foodId);
    } else {
      newExpanded.add(foodId);
    }
    setExpandedRows(newExpanded);
  };
  
  const MicronutrientPanel = ({ food }) => {
    const { vitamins = {}, minerals = {}, electrolytes = {}, fatty_acids = {} } = food;
    
    return (
      <tr className="bg-blue-50/50">
        <td colSpan="8" className="px-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Vitamins */}
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Vitamins
              </h4>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span>Vitamin A:</span><span className="font-medium">{formatNumber(vitamins.vitamin_a_mcg || 0)} mcg</span></div>
                <div className="flex justify-between"><span>Vitamin D3:</span><span className="font-medium">{formatNumber(vitamins.vitamin_d3_mcg || 0)} mcg</span></div>
                <div className="flex justify-between"><span>Vitamin E:</span><span className="font-medium">{formatNumber(vitamins.vitamin_e_mcg || 0)} mcg</span></div>
                <div className="flex justify-between"><span>Vitamin K:</span><span className="font-medium">{formatNumber(vitamins.vitamin_k_mcg || 0)} mcg</span></div>
                <div className="flex justify-between"><span>Vitamin B1:</span><span className="font-medium">{formatNumber(vitamins.vitamin_b1_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Vitamin B2:</span><span className="font-medium">{formatNumber(vitamins.vitamin_b2_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Vitamin B3:</span><span className="font-medium">{formatNumber(vitamins.vitamin_b3_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Vitamin B5:</span><span className="font-medium">{formatNumber(vitamins.vitamin_b5_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Vitamin B6:</span><span className="font-medium">{formatNumber(vitamins.vitamin_b6_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Vitamin B12:</span><span className="font-medium">{formatNumber(vitamins.vitamin_b12_mcg || 0)} mcg</span></div>
                <div className="flex justify-between"><span>Folic Acid:</span><span className="font-medium">{formatNumber(vitamins.folic_acid_mcg || 0)} mcg</span></div>
                <div className="flex justify-between"><span>Biotin:</span><span className="font-medium">{formatNumber(vitamins.biotin_mcg || 0)} mcg</span></div>
                <div className="flex justify-between"><span>Choline:</span><span className="font-medium">{formatNumber(vitamins.choline_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Vitamin C:</span><span className="font-medium">{formatNumber(vitamins.vitamin_c_mg || 0)} mg</span></div>
              </div>
            </div>
            
            {/* Minerals */}
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                Minerals
              </h4>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span>Calcium:</span><span className="font-medium">{formatNumber(minerals.calcium_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Phosphorus:</span><span className="font-medium">{formatNumber(minerals.phosphorus_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Magnesium:</span><span className="font-medium">{formatNumber(minerals.magnesium_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Iron (Fe):</span><span className="font-medium">{formatNumber(minerals.iron_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Iodine:</span><span className="font-medium">{formatNumber(minerals.iodine_mcg || 0)} mcg</span></div>
                <div className="flex justify-between"><span>Zinc:</span><span className="font-medium">{formatNumber(minerals.zinc_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Selenium:</span><span className="font-medium">{formatNumber(minerals.selenium_mcg || 0)} mcg</span></div>
                <div className="flex justify-between"><span>Manganese:</span><span className="font-medium">{formatNumber(minerals.manganese_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Fluoride:</span><span className="font-medium">{formatNumber(minerals.fluoride_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Chromium:</span><span className="font-medium">{formatNumber(minerals.chromium_mcg || 0)} mcg</span></div>
                <div className="flex justify-between"><span>Copper (Cu):</span><span className="font-medium">{formatNumber(minerals.copper_mg || 0)} mg</span></div>
              </div>
            </div>
            
            {/* Electrolytes & Fatty Acids */}
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Electrolytes
              </h4>
              <div className="space-y-1 text-xs mb-4">
                <div className="flex justify-between"><span>Sodium (Na):</span><span className="font-medium">{formatNumber(electrolytes.sodium_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Potassium (K):</span><span className="font-medium">{formatNumber(electrolytes.potassium_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Chloride (Cl):</span><span className="font-medium">{formatNumber(electrolytes.chloride_mg || 0)} mg</span></div>
              </div>
              
              <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                Fatty Acids
              </h4>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span>Omega-3:</span><span className="font-medium">{formatNumber(fatty_acids.omega3_mg || 0)} mg</span></div>
                <div className="flex justify-between"><span>Omega-6:</span><span className="font-medium">{formatNumber(fatty_acids.omega6_mg || 0)} mg</span></div>
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };
  
  const handleUpdateFood = async () => {
    if (!editFood.name || !editFood.energy) {
      showAlert({
        type: 'warning',
        title: 'Missing Information',
        message: 'Please enter at least name and energy'
      });
      return;
    }

    setEditing(true);
    try {
      await foodAPI.update(editingFood.id, {
        name: editFood.name,
        unit: editFood.unit,
        macronutrients: {
          energy_kcal: parseFloat(editFood.energy),
          protein_g: parseFloat(editFood.protein || 0),
          carbohydrate_g: parseFloat(editFood.carbs || 0),
          fat_g: parseFloat(editFood.fat || 0),
          fiber_g: parseFloat(editFood.fiber || 0),
        },
      });

      showAlert({
        type: 'success',
        title: 'Success',
        message: 'Food updated successfully!'
      });
      setShowEditDialog(false);
      setEditingFood(null);
      setEditFood({ name: '', unit: '', energy: '', protein: '', carbs: '', fat: '', fiber: '' });
      await loadFoods();
    } catch (error) {
      console.error('Error updating food:', error);
      showAlert({
        type: 'danger',
        title: 'Error',
        message: 'Failed to update food'
      });
    } finally {
      setEditing(false);
    }
  };

  // Excel Handlers for Admin
  const handleExportExcel = () => {
    try {
      exportFoodsToExcel(foods);
      showAlert({
        type: 'success',
        title: 'Berhasil Ekspor',
        message: `Berhasil mengekspor ${foods.length} data makanan ke file Excel.`
      });
    } catch (err) {
      showAlert({
        type: 'danger',
        title: 'Gagal Ekspor',
        message: err.message || 'Terjadi kesalahan saat mengekspor Excel'
      });
    }
  };

  const handleDownloadTemplate = () => {
    try {
      downloadFoodTemplateExcel();
    } catch (err) {
      showAlert({
        type: 'danger',
        title: 'Gagal Unduh Template',
        message: err.message || 'Terjadi kesalahan saat mengunduh template Excel'
      });
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportError('');
    try {
      const result = await parseFoodExcel(file);
      setParsedImportData(result.items);
    } catch (err) {
      setImportError(err.message || 'Gagal membaca file Excel');
      setParsedImportData(null);
    }
  };

  const handleExecuteImport = async () => {
    if (!parsedImportData || parsedImportData.length === 0) return;
    setImporting(true);
    try {
      const res = await foodAPI.bulkImport(parsedImportData);
      showAlert({
        type: 'success',
        title: 'Import Berhasil',
        message: res.message || `Berhasil mengimpor ${parsedImportData.length} data makanan ke database.`
      });
      setShowImportDialog(false);
      setImportFile(null);
      setParsedImportData(null);
      await loadFoods();
    } catch (err) {
      showAlert({
        type: 'danger',
        title: 'Gagal Import',
        message: err.response?.data?.detail || err.message || 'Terjadi kesalahan saat menyimpan ke database'
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col justify-between">
      <div>
        <Navbar 
          user={user} 
          onLogout={() => { localStorage.removeItem('token'); navigate('/'); }} 
          breadcrumbs={[{ label: 'Database Makanan' }]} 
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Database Makanan</h1>
              <p className="text-sm text-slate-500 mt-1">
                Tersedia {filteredFoods.length} dari {foods.length} bahan makanan oral dan formula enteral.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              {isAdmin && (
                <>
                  <Button 
                    variant="outline" 
                    onClick={handleDownloadTemplate}
                    className="border-slate-200 text-slate-700 hover:bg-slate-100/80 text-xs sm:text-sm shadow-2xs"
                    title="Unduh format template Excel untuk import data"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" />
                    <span>Template Excel</span>
                  </Button>

                  <Button 
                    variant="outline" 
                    onClick={handleExportExcel}
                    className="border-slate-200 text-slate-700 hover:bg-slate-100/80 text-xs sm:text-sm shadow-2xs"
                    title="Ekspor seluruh database makanan ke file Excel"
                  >
                    <FileDown className="h-4 w-4 mr-1.5 text-emerald-600" />
                    <span>Export Excel</span>
                  </Button>

                  <Button 
                    variant="outline" 
                    onClick={() => setShowImportDialog(true)}
                    className="border-emerald-300 bg-emerald-50/50 text-emerald-700 hover:bg-emerald-100/80 text-xs sm:text-sm shadow-2xs"
                    title="Impor data makanan dari file Excel ke database"
                  >
                    <FileUp className="h-4 w-4 mr-1.5 text-emerald-600" />
                    <span>Import Excel</span>
                  </Button>
                </>
              )}

              <Button 
                onClick={() => setShowAddDialog(true)} 
                className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs text-xs sm:text-sm"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                <span>Tambah Makanan</span>
              </Button>
            </div>
          </div>

        {/* Search */}
        <Card>
          <CardHeader>
            <CardTitle>Search Foods</CardTitle>
            <CardDescription>Search from 1,740+ food items</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardContent>
        </Card>

        {/* Foods Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-4">
                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-2.5 border-b border-slate-100 animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="h-4 w-4 bg-slate-200 rounded" />
                      <div className="h-4 w-44 bg-slate-200 rounded" />
                    </div>
                    <div className="h-4 w-16 bg-slate-100 rounded hidden sm:block" />
                    <div className="h-4 w-16 bg-slate-200 rounded font-medium" />
                    <div className="h-4 w-12 bg-slate-100 rounded hidden md:block" />
                    <div className="h-4 w-12 bg-slate-100 rounded hidden md:block" />
                    <div className="h-4 w-12 bg-slate-100 rounded hidden md:block" />
                    <div className="h-6 w-16 bg-slate-100 rounded-full" />
                    <div className="h-8 w-16 bg-slate-100 rounded-lg" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50/90 border-b border-slate-200 text-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold w-10"></th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Food Name</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Unit</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Energy (Kcal)</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Protein (g)</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Carbs (g)</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Fat (g)</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Type</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFoods.map((food) => (
                        <React.Fragment key={food.id}>
                          <tr className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                            <td className="px-4 py-3">
                              <button
                                onClick={() => toggleExpand(food.id)}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                                title="Show micronutrients"
                              >
                                {expandedRows.has(food.id) ? (
                                  <ChevronDown className="h-4 w-4 text-emerald-600" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-gray-400" />
                                )}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-slate-900">{food.name}</td>
                            <td className="px-4 py-3 text-sm text-slate-600">{food.unit}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold text-slate-800">{Math.round(food.macronutrients.energy_kcal)}</td>
                            <td className="px-4 py-3 text-sm text-right text-slate-700">{formatNumber(food.macronutrients.protein_g)}</td>
                            <td className="px-4 py-3 text-sm text-right text-slate-700">{formatNumber(food.macronutrients.carbohydrate_g)}</td>
                            <td className="px-4 py-3 text-sm text-right text-slate-700">{formatNumber(food.macronutrients.fat_g)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                                food.is_custom ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'
                              }`}>
                                {food.is_custom ? 'Custom' : 'System'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {isAdmin && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleEdit(food)}
                                      title="Edit macronutrients"
                                    >
                                      <Edit className="h-4 w-4 text-blue-600" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openMicroEditor(food)}
                                      title="Edit micronutrients"
                                    >
                                      <Beaker className="h-4 w-4 text-purple-600" />
                                    </Button>
                                  </>
                                )}
                                {(isAdmin || food.is_custom) && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDelete(food.id, food.name, food.is_custom)}
                                    title="Delete food"
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {expandedRows.has(food.id) && <MicronutrientPanel food={food} />}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {filteredFoods.map((food) => (
                    <div key={food.id} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                      {/* Main Card */}
                      <div className="p-3">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900">{food.name}</p>
                            <p className="text-xs text-gray-500">{food.unit}</p>
                          </div>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ml-2 flex-shrink-0 ${
                            food.is_custom ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'
                          }`}>
                            {food.is_custom ? 'Custom' : 'System'}
                          </span>
                        </div>
                        
                        {/* Macronutrients Grid */}
                        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                          <div className="bg-amber-50 px-2 py-1 rounded">
                            <span className="text-gray-600">Energy:</span>
                            <span className="font-semibold ml-1">{Math.round(food.macronutrients.energy_kcal)} Kcal</span>
                          </div>
                          <div className="bg-blue-50 px-2 py-1 rounded">
                            <span className="text-gray-600">Protein:</span>
                            <span className="font-semibold ml-1">{formatNumber(food.macronutrients.protein_g)}g</span>
                          </div>
                          <div className="bg-green-50 px-2 py-1 rounded">
                            <span className="text-gray-600">Carbs:</span>
                            <span className="font-semibold ml-1">{formatNumber(food.macronutrients.carbohydrate_g)}g</span>
                          </div>
                          <div className="bg-red-50 px-2 py-1 rounded">
                            <span className="text-gray-600">Fat:</span>
                            <span className="font-semibold ml-1">{formatNumber(food.macronutrients.fat_g)}g</span>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-2 border-t border-gray-100">
                          <button
                            onClick={() => toggleExpand(food.id)}
                            className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium"
                          >
                            {expandedRows.has(food.id) ? (
                              <>
                                <ChevronDown className="h-4 w-4" />
                                Hide Details
                              </>
                            ) : (
                              <>
                                <ChevronRight className="h-4 w-4" />
                                Show Details
                              </>
                            )}
                          </button>
                          
                          {isAdmin && (
                            <>
                              <button
                                onClick={() => handleEdit(food)}
                                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 ml-auto"
                                title="Edit macronutrients"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => openMicroEditor(food)}
                                className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700"
                                title="Edit micronutrients"
                              >
                                <Beaker className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {(isAdmin || food.is_custom) && (
                            <button
                              onClick={() => handleDelete(food.id, food.name, food.is_custom)}
                              className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                              title="Delete food"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded Micronutrient Panel */}
                      {expandedRows.has(food.id) && (
                        <div className="border-t border-gray-200 bg-gray-50 p-3">
                          <MicronutrientPanel food={food} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {filteredFoods.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    No foods found
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Custom Food Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Custom Food</DialogTitle>
            <DialogDescription>Add your own food item to the database</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Food Name *</Label>
              <Input
                value={newFood.name}
                onChange={(e) => setNewFood({ ...newFood, name: e.target.value })}
                placeholder="e.g., Homemade Pizza 100g"
              />
            </div>
            <div>
              <Label>Unit</Label>
              <Input
                value={newFood.unit}
                onChange={(e) => setNewFood({ ...newFood, unit: e.target.value })}
                placeholder="e.g., 100g, 1 serving"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Energy (Kcal) *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newFood.energy}
                  onChange={(e) => setNewFood({ ...newFood, energy: e.target.value })}
                  placeholder="250"
                />
              </div>
              <div>
                <Label>Protein (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newFood.protein}
                  onChange={(e) => setNewFood({ ...newFood, protein: e.target.value })}
                  placeholder="12"
                />
              </div>
              <div>
                <Label>Carbs (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newFood.carbs}
                  onChange={(e) => setNewFood({ ...newFood, carbs: e.target.value })}
                  placeholder="30"
                />
              </div>
              <div>
                <Label>Fat (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newFood.fat}
                  onChange={(e) => setNewFood({ ...newFood, fat: e.target.value })}
                  placeholder="10"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddCustomFood} disabled={adding}>
              {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Food
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Food Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Food</DialogTitle>
            <DialogDescription>Update food item information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Food Name *</Label>
              <Input
                value={editFood.name}
                onChange={(e) => setEditFood({ ...editFood, name: e.target.value })}
                placeholder="e.g., Nasi Putih"
              />
            </div>
            <div>
              <Label>Unit</Label>
              <Input
                value={editFood.unit}
                onChange={(e) => setEditFood({ ...editFood, unit: e.target.value })}
                placeholder="e.g., 100g, 1 cup"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Energy (Kcal) *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editFood.energy}
                  onChange={(e) => setEditFood({ ...editFood, energy: e.target.value })}
                  placeholder="130"
                />
              </div>
              <div>
                <Label>Protein (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editFood.protein}
                  onChange={(e) => setEditFood({ ...editFood, protein: e.target.value })}
                  placeholder="2.7"
                />
              </div>
              <div>
                <Label>Carbs (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editFood.carbs}
                  onChange={(e) => setEditFood({ ...editFood, carbs: e.target.value })}
                  placeholder="28.0"
                />
              </div>
              <div>
                <Label>Fat (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editFood.fat}
                  onChange={(e) => setEditFood({ ...editFood, fat: e.target.value })}
                  placeholder="0.3"
                />
              </div>
              <div>
                <Label>Fiber (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editFood.fiber}
                  onChange={(e) => setEditFood({ ...editFood, fiber: e.target.value })}
                  placeholder="0.4"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateFood} disabled={editing}>
              {editing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update Food
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm}
        type={confirmDialog.type}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={closeAlert}
        type={alertDialog.type}
        title={alertDialog.title}
        message={alertDialog.message}
        buttonText={alertDialog.buttonText}
      />
      
      {/* Micronutrient Editor Modal */}
      {showMicroEditor && editingMicroFood && (
        <Dialog open={showMicroEditor} onOpenChange={setShowMicroEditor}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Micronutrients - {editingMicroFood.name}</DialogTitle>
              <DialogDescription>Update micronutrient values (per {editingMicroFood.unit})</DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-4">
              {/* Vitamins Section */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                  Vitamins
                </h4>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Vitamin A (mcg)</Label>
                    <Input type="number" step="0.1" value={microData.vitamins.vitamin_a_mcg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, vitamin_a_mcg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Vitamin D3 (mcg)</Label>
                    <Input type="number" step="0.1" value={microData.vitamins.vitamin_d3_mcg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, vitamin_d3_mcg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Vitamin E (mcg)</Label>
                    <Input type="number" step="0.1" value={microData.vitamins.vitamin_e_mcg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, vitamin_e_mcg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Vitamin K (mcg)</Label>
                    <Input type="number" step="0.1" value={microData.vitamins.vitamin_k_mcg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, vitamin_k_mcg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Vitamin B1 (mg)</Label>
                    <Input type="number" step="0.01" value={microData.vitamins.vitamin_b1_mg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, vitamin_b1_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Vitamin B2 (mg)</Label>
                    <Input type="number" step="0.01" value={microData.vitamins.vitamin_b2_mg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, vitamin_b2_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Vitamin B3 (mg)</Label>
                    <Input type="number" step="0.01" value={microData.vitamins.vitamin_b3_mg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, vitamin_b3_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Vitamin B5 (mg)</Label>
                    <Input type="number" step="0.01" value={microData.vitamins.vitamin_b5_mg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, vitamin_b5_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Vitamin B6 (mg)</Label>
                    <Input type="number" step="0.01" value={microData.vitamins.vitamin_b6_mg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, vitamin_b6_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Vitamin B12 (mcg)</Label>
                    <Input type="number" step="0.1" value={microData.vitamins.vitamin_b12_mcg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, vitamin_b12_mcg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Folic Acid (mcg)</Label>
                    <Input type="number" step="0.1" value={microData.vitamins.folic_acid_mcg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, folic_acid_mcg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Biotin (mcg)</Label>
                    <Input type="number" step="0.1" value={microData.vitamins.biotin_mcg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, biotin_mcg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Choline (mg)</Label>
                    <Input type="number" step="0.1" value={microData.vitamins.choline_mg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, choline_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Vitamin C (mg)</Label>
                    <Input type="number" step="0.1" value={microData.vitamins.vitamin_c_mg || 0} onChange={(e) => setMicroData({...microData, vitamins: {...microData.vitamins, vitamin_c_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                </div>
              </div>
              
              {/* Minerals Section */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                  Minerals
                </h4>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Calcium (mg)</Label>
                    <Input type="number" step="0.1" value={microData.minerals.calcium_mg || 0} onChange={(e) => setMicroData({...microData, minerals: {...microData.minerals, calcium_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Phosphorus (mg)</Label>
                    <Input type="number" step="0.1" value={microData.minerals.phosphorus_mg || 0} onChange={(e) => setMicroData({...microData, minerals: {...microData.minerals, phosphorus_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Magnesium (mg)</Label>
                    <Input type="number" step="0.1" value={microData.minerals.magnesium_mg || 0} onChange={(e) => setMicroData({...microData, minerals: {...microData.minerals, magnesium_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Iron (mg)</Label>
                    <Input type="number" step="0.01" value={microData.minerals.iron_mg || 0} onChange={(e) => setMicroData({...microData, minerals: {...microData.minerals, iron_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Iodine (mcg)</Label>
                    <Input type="number" step="0.1" value={microData.minerals.iodine_mcg || 0} onChange={(e) => setMicroData({...microData, minerals: {...microData.minerals, iodine_mcg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Zinc (mg)</Label>
                    <Input type="number" step="0.01" value={microData.minerals.zinc_mg || 0} onChange={(e) => setMicroData({...microData, minerals: {...microData.minerals, zinc_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Selenium (mcg)</Label>
                    <Input type="number" step="0.1" value={microData.minerals.selenium_mcg || 0} onChange={(e) => setMicroData({...microData, minerals: {...microData.minerals, selenium_mcg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Manganese (mg)</Label>
                    <Input type="number" step="0.01" value={microData.minerals.manganese_mg || 0} onChange={(e) => setMicroData({...microData, minerals: {...microData.minerals, manganese_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Fluoride (mg)</Label>
                    <Input type="number" step="0.01" value={microData.minerals.fluoride_mg || 0} onChange={(e) => setMicroData({...microData, minerals: {...microData.minerals, fluoride_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Chromium (mcg)</Label>
                    <Input type="number" step="0.1" value={microData.minerals.chromium_mcg || 0} onChange={(e) => setMicroData({...microData, minerals: {...microData.minerals, chromium_mcg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Copper (mg)</Label>
                    <Input type="number" step="0.01" value={microData.minerals.copper_mg || 0} onChange={(e) => setMicroData({...microData, minerals: {...microData.minerals, copper_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                </div>
              </div>
              
              {/* Electrolytes & Fatty Acids Section */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  Electrolytes
                </h4>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Sodium (mg)</Label>
                    <Input type="number" step="0.1" value={microData.electrolytes.sodium_mg || 0} onChange={(e) => setMicroData({...microData, electrolytes: {...microData.electrolytes, sodium_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Potassium (mg)</Label>
                    <Input type="number" step="0.1" value={microData.electrolytes.potassium_mg || 0} onChange={(e) => setMicroData({...microData, electrolytes: {...microData.electrolytes, potassium_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Chloride (mg)</Label>
                    <Input type="number" step="0.1" value={microData.electrolytes.chloride_mg || 0} onChange={(e) => setMicroData({...microData, electrolytes: {...microData.electrolytes, chloride_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                </div>
                
                <h4 className="font-semibold text-sm flex items-center gap-2 pt-4">
                  <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
                  Fatty Acids
                </h4>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Omega-3 (mg)</Label>
                    <Input type="number" step="0.1" value={microData.fatty_acids.omega3_mg || 0} onChange={(e) => setMicroData({...microData, fatty_acids: {...microData.fatty_acids, omega3_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Omega-6 (mg)</Label>
                    <Input type="number" step="0.1" value={microData.fatty_acids.omega6_mg || 0} onChange={(e) => setMicroData({...microData, fatty_acids: {...microData.fatty_acids, omega6_mg: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMicroEditor(false)} disabled={savingMicro}>
                Cancel
              </Button>
              <Button onClick={handleSaveMicronutrients} disabled={savingMicro} className="bg-green-600 hover:bg-green-700">
                {savingMicro ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Micronutrients'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Excel Import Dialog for Admin */}
      {isAdmin && (
        <Dialog open={showImportDialog} onOpenChange={(open) => {
          setShowImportDialog(open);
          if (!open) {
            setImportFile(null);
            setParsedImportData(null);
            setImportError('');
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-900">
                <FileUp className="h-5 w-5 text-emerald-600" />
                <span>Impor Database Makanan dari Excel</span>
              </DialogTitle>
              <DialogDescription>
                Unggah file Excel (.xlsx) sesuai template NUTRIMUS. Data makanan yang diimpor akan menjadi master data sistem.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* File upload area */}
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-emerald-400 transition-colors bg-slate-50/50">
                <input 
                  type="file" 
                  accept=".xlsx, .xls" 
                  onChange={handleFileChange} 
                  id="excel-food-file-input"
                  className="hidden" 
                />
                <label htmlFor="excel-food-file-input" className="cursor-pointer block">
                  <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
                    <Upload className="h-6 w-6" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800">
                    {importFile ? importFile.name : 'Pilih File Excel (.xlsx)'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {importFile ? `${(importFile.size / 1024).toFixed(1)} KB` : 'Klik untuk mencari file atau seret file ke sini'}
                  </p>
                </label>
              </div>

              {/* Error message */}
              {importError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="font-semibold">Kesalahan membaca file:</strong> {importError}
                  </div>
                </div>
              )}

              {/* Preview parsed data */}
              {parsedImportData && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-emerald-700 flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>{parsedImportData.length} item makanan valid terdeteksi</span>
                    </span>
                    <span className="text-slate-400">Pratinjau 5 data pertama:</span>
                  </div>

                  <div className="border border-slate-200 rounded-xl overflow-x-auto text-xs max-h-48">
                    <table className="w-full">
                      <thead className="bg-slate-100 text-slate-700 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Nama Makanan</th>
                          <th className="px-3 py-2 text-left">Satuan</th>
                          <th className="px-3 py-2 text-right">Energi</th>
                          <th className="px-3 py-2 text-right">Protein</th>
                          <th className="px-3 py-2 text-right">KH</th>
                          <th className="px-3 py-2 text-right">Lemak</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {parsedImportData.slice(0, 5).map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="px-3 py-1.5 font-medium text-slate-900">{item.name}</td>
                            <td className="px-3 py-1.5 text-slate-600">{item.unit}</td>
                            <td className="px-3 py-1.5 text-right font-semibold">{item.macronutrients.energy_kcal} Kcal</td>
                            <td className="px-3 py-1.5 text-right">{item.macronutrients.protein_g}g</td>
                            <td className="px-3 py-1.5 text-right">{item.macronutrients.carbohydrate_g}g</td>
                            <td className="px-3 py-1.5 text-right">{item.macronutrients.fat_g}g</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportDialog(false);
                  setImportFile(null);
                  setParsedImportData(null);
                  setImportError('');
                }}
                disabled={importing}
              >
                Batal
              </Button>
              <Button
                onClick={handleExecuteImport}
                disabled={!parsedImportData || parsedImportData.length === 0 || importing}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    <span>Menyimpan ke Database...</span>
                  </>
                ) : (
                  <>
                    <FileUp className="h-4 w-4 mr-2" />
                    <span>Mulai Impor ({parsedImportData ? parsedImportData.length : 0} Item)</span>
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      </div>
      
      <Footer />
      
      {/* Back to Top Button */}
      <BackToTop />
    </div>
  );
}
